import { useState } from 'react';
import { User, Calendar, MessageCircle, Clock, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembersPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/members/login`, credentials);
      if (response.data.success) {
        setMember(response.data.member);
        setIsAuthenticated(true);
        localStorage.setItem('velmont_member', JSON.stringify(response.data.member));
        toast.success('Welcome back');
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('velmont_member');
    setIsAuthenticated(false);
    setMember(null);
  };

  // Mock experiences for demonstration
  const pastExperiences = [
    {
      id: 1,
      title: 'São Paulo Night Out',
      date: 'November 2024',
      description: 'VIP table at D-Edge, private transfer, after-party access'
    },
    {
      id: 2,
      title: 'Rio Helicopter Tour',
      date: 'October 2024',
      description: 'Sunset flight over Sugarloaf, champagne service'
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-4 font-manrope font-medium">
              Members Only
            </p>
            <h1 className="font-playfair text-3xl text-[#F5F5F0] mb-3">Welcome Back</h1>
            <p className="text-[#52525B] text-sm font-manrope">Access your private portal</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                Email
              </label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                placeholder="your@email.com"
                data-testid="member-email"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                Password
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                placeholder="••••••••"
                data-testid="member-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-black py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all disabled:opacity-50 mt-8"
              data-testid="member-login-btn"
            >
              {loading ? 'Accessing...' : 'Enter Portal'}
            </button>
          </form>
          
          <p className="text-center text-[#3F3F46] text-xs mt-8 font-manrope">
            Membership is by invitation only.<br />
            Contact your concierge for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="members-portal" className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-16">
          <div>
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-3 font-manrope font-medium">
              Private Portal
            </p>
            <h1 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
              Welcome, <span className="italic">{member?.name}</span>
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="mt-4 md:mt-0 text-[#52525B] text-xs uppercase tracking-widest hover:text-gold transition-colors"
          >
            Sign Out
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Actions */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <h2 className="font-playfair text-xl text-[#F5F5F0] mb-6">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="/contact"
                  className="flex items-center justify-between p-4 border border-white/5 hover:border-gold/30 transition-colors group"
                >
                  <div className="flex items-center gap-4">
                    <Calendar className="text-gold" size={20} />
                    <span className="text-[#F5F5F0] font-manrope">Plan New Experience</span>
                  </div>
                  <ChevronRight className="text-[#52525B] group-hover:text-gold transition-colors" size={16} />
                </a>
                <a
                  href="#consultation"
                  className="flex items-center justify-between p-4 border border-white/5 hover:border-gold/30 transition-colors group"
                >
                  <div className="flex items-center gap-4">
                    <MessageCircle className="text-gold" size={20} />
                    <span className="text-[#F5F5F0] font-manrope">Schedule Call</span>
                  </div>
                  <ChevronRight className="text-[#52525B] group-hover:text-gold transition-colors" size={16} />
                </a>
              </div>
            </div>

            {/* Past Experiences */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <h2 className="font-playfair text-xl text-[#F5F5F0] mb-6">Your Experiences</h2>
              {pastExperiences.length > 0 ? (
                <div className="space-y-4">
                  {pastExperiences.map((exp) => (
                    <div key={exp.id} className="p-4 border border-white/5 hover:border-gold/20 transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-[#F5F5F0] font-manrope font-medium">{exp.title}</h3>
                          <p className="text-[#A1A1AA] text-sm mt-1">{exp.description}</p>
                        </div>
                        <span className="text-[#52525B] text-xs">{exp.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#52525B] text-sm">No experiences yet. Let's plan your first.</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Profile Card */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <User className="text-gold" size={20} />
                </div>
                <div>
                  <p className="text-[#F5F5F0] font-manrope">{member?.name}</p>
                  <p className="text-[#52525B] text-xs">{member?.email}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-white/5">
                <p className="text-gold text-xs uppercase tracking-widest mb-2">Member Since</p>
                <p className="text-[#A1A1AA] text-sm">2024</p>
              </div>
            </div>

            {/* Direct Contact */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <h3 className="font-playfair text-lg text-[#F5F5F0] mb-4">Your Concierge</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">
                Reach out anytime. We're available 24/7 for our members.
              </p>
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 border border-gold text-gold text-xs uppercase tracking-widest font-bold hover:bg-gold hover:text-black transition-all"
              >
                <MessageCircle size={14} />
                WhatsApp
              </a>
            </div>

            {/* Book Consultation */}
            <div id="consultation" className="bg-obsidian-paper border border-gold/20 p-8">
              <h3 className="font-playfair text-lg text-[#F5F5F0] mb-4">Schedule a Call</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">
                Book a private consultation with your dedicated concierge.
              </p>
              <a
                href="https://cal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-gold text-black text-xs uppercase tracking-widest font-bold hover:bg-gold-light transition-all"
              >
                <Clock size={14} />
                Book Time
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembersPortal;
